/**
 *
 * AbsenceDetail
 *
 */

import React, { memo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';

// styles
import "./style.scss";

import { useInjectSaga } from "utils/injectSaga";
import { useInjectReducer } from "utils/injectReducer";
import reducer from "./reducer";
import saga from "./saga";

import {
  flushStateAbsenceDetail,
  loadAbsence,
  changeAbsence,
  deleteAbsence,
} from "./actions";

import {
  selectAbsenceData,
  selectAbsenceLoadingUpdate,
  selectAbsenceLoading,
  selectAbsenceError,
  selectAbsenceDeleteSuccessful,
} from "./selectors";

// antd component
import { Layout, Form, Breadcrumb, Tag } from "antd";
const { Content } = Layout;

//components
import Preloader from "components/Preloader";
import CustomPageHeader from "components/CustomPageHeader";
import CreateAbsence from "components/Absences/CreateAbsence";

// init forms
import { initFormsClasses } from "utils/forms/forms.init";
// helper for forms
import { setFormValues } from "utils/forms/forms.utils";

// utils
import { getAbsenceDuration } from "utils/helpers/helper.generateData";
import { getObjId } from 'utils/helpers/helper.general';
import { convertDateToWeekDay, convertTimeToString, convertDateToString } from "utils/moment/moment.utils";


export function AbsenceDetail({
  // main data
  absenceData,
  // states
  deleteSuccessful,
  error,
  loading,
  history,
  loadingUpdate,
  // actions
  loadAbsence,
  deleteAbsence,
  changeAbsence,
  flushState,
  //default
  className,
}) {
  useInjectReducer({ key: "absenceDetail", reducer });
  useInjectSaga({ key: "absenceDetail", saga });

  const { t, i18n } = useTranslation();

  const newKey = "new";

  const { id: initId } = useParams();

  const [breadcrumb, setBreadcrumb] = useState([]);

  const generalForm = useCallback(() => initFormsClasses(), [i18n?.language]);
  // Form initialization for each tab
  const [customForm] = Form.useForm();

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    // reset data in the general form
    customForm.resetFields();

    if (!initId || initId == newKey) {
      // Set Breadcrumbs for new absence
      setBreadcrumb([
        {
          path: "/absences",
          exact: true,
          breadcrumbName: t("labels.absences"),
        },
        {
          path: `/absences/${newKey}`,
          breadcrumbName: t("absences.newAbsence"),
        },
      ]);
    } else if (initId && initId !== newKey) {
      loadAbsence(initId);
    }
  }, [initId]);

  useEffect(() => {
    if (absenceData) {
      const mainDate = convertDateToString(absenceData?.date_from);
      const time = convertTimeToString(absenceData?.time_from);
      const duration = absenceData?.timeDuration && getAbsenceDuration(absenceData);
      const absenceDateString =
        initId !== newKey
          ? ` • ${convertDateToWeekDay(absenceData?.date_from, "dd")}, ${mainDate} ${time} ${!!duration ? "• " + duration:""}`
          : "";

      // Set Breadcrumbs
      setBreadcrumb([
        {
          path: "/absences",
          exact: true,
          breadcrumbName: t("labels.absences"),
        },
        {
          path: `/absences/${getObjId(absenceData)}`,
          breadcrumbName: `${t("labels.absence")}${absenceDateString}`,
        },
      ]);

      // reset data in the general form
      setFormValues(customForm, generalForm(), absenceData);
    }
  }, [absenceData, initId, i18n?.language]);

  // Clearing the state after unmounting a component
  // Anything in here is fired on component unmount.
  useEffect(() => {
    return () => {
      flushState();
      setBreadcrumb([]);
    };
  }, []);

  const itemBreadcrumbRender = (route, params, routes, paths) => {
    const last = routes.indexOf(route) === routes.length - 1;
    return last ? (
      <span>{route.breadcrumbName}</span>
    ) : (
      <Link to={route.path}>{route.breadcrumbName}</Link>
    );
  };

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>{t("labels.absence")}</title>
        <meta name="description" content="Description of Absence" />
      </Helmet>
      <Content className="main-content-container absence-detail-wrapper">
        {!initId && loading && <Preloader />}
        {!loading && initId && (
          <>
            {breadcrumb && (
              <CustomPageHeader
                className="absence-header"
                title={
                  <>
                    <Breadcrumb
                      className="absence-breadcrumb"
                      routes={breadcrumb}
                      itemRender={itemBreadcrumbRender}
                    />
                    {(!!absenceData?.origin ||
                      !!absenceData?.original ||
                      !!absenceData?.unjustified) && (
                      <span className="tags-abs">
                        {!!absenceData?.original && (
                          <Tag className="abs-tag original">
                            {t("absences.recurring")}
                          </Tag>
                        )}
                        {!!absenceData?.origin && (
                          <Tag className="abs-tag origin">
                            {t("absences.reccurence")}
                          </Tag>
                        )}
                        {!!absenceData?.unjustified && (
                          <Tag className="abs-tag origin">
                            {t("global.absenceTypes.unjustified")}
                          </Tag>
                        )}
                      </span>
                    )}
                  </>
                }
                subTitlePosition="top"
              />
            )}

            <div className="absence-detail-wrapper__content">
              {!!absenceData?.origin && (
                <Link
                  className="abs-tag-link"
                  to={`/absences/${getObjId(absenceData?.origin)}`}
                >
                  <Tag className="abs-tag original">
                    {t("absences.recurring")}
                  </Tag>
                </Link>
              )}
              <CreateAbsence
                isNew={initId == newKey}
                absence={absenceData}
                loading={loadingUpdate}
                change={changeAbsence}
              />
            </div>
          </>
        )}
      </Content>
    </Layout>
  );
}

AbsenceDetail.defaultProps = {
  loading: false,
};
AbsenceDetail.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: selectAbsenceLoading(),
  error: selectAbsenceError(),
  deleteSuccessful: selectAbsenceDeleteSuccessful(),
  absenceData: selectAbsenceData(),
  loadingUpdate: selectAbsenceLoadingUpdate(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushStateAbsenceDetail()),
    loadAbsence: id => dispatch(loadAbsence(id)),
    changeAbsence: data => dispatch(changeAbsence(data)),
    deleteAbsence: id => dispatch(deleteAbsence(id)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(AbsenceDetail);
